<template>
    <div class="account-switcher">
        <Button
            ref="anchor"
            :class="[
                'p-button p-button-text p-button-rounded account-btn',
                { 'account-btn--dense': dense }
            ]"
            :aria-expanded="panelVisible.toString()"
            aria-haspopup="true"
            @click="toggle($event)"
        >
            <!-- Logo con fade/slide al cambiar -->
            <Transition name="swap" mode="out-in">
                <ActivityLogo
                    :key="logoKey"
                    :activity="currentActivity"
                    alt="Logo actividad"
                    :imgClass="['acc-logo', { 'acc-logo--dense': dense }, 'mr-2']"
                    initialsClass="mr-2"
                    :size="dense ? 28 : 32"
                />
            </Transition>

            <!-- Nombre con fade/slide al cambiar -->
            <Transition name="swap" mode="out-in">
                <span
                    :key="activityName"
                    :class="['acc-name', { 'acc-name--dense': dense }, 'mr-2']"
                >
                    {{ activityName }}
                </span>
            </Transition>

            <!-- Caret por defecto -->
            <i v-if="!showCheck" :class="['pi', panelVisible ? 'pi-chevron-up' : 'pi-chevron-down']"></i>

            <!-- Check animado al cambiar (muta y vuelve a caret) -->
            <span v-else class="acc-check" aria-hidden="true">
                <i class="pi pi-check"></i>
            </span>

            <!-- Mensaje accesible (no visible) -->
            <span class="sr-only" aria-live="polite">
                {{ successMessage }}
            </span>
        </Button>

        <OverlayPanel
            ref="panel"
            :dismissable="true"
            appendTo="body"
            class="account-panel"
            @show="panelVisible = true"
            @hide="panelVisible = false"
        >
            <!-- Cuenta actual -->
            <div class="p-2 surface-50 border-round-lg flex align-items-center">
                <Transition name="swap" mode="out-in">
                    <ActivityLogo
                        :key="logoKey"
                        :activity="currentActivity"
                        :imgClass="['acc-logo', { 'acc-logo--dense': dense }, 'mr-2']"
                        initialsClass="mr-2"
                        :size="dense ? 28 : 32"
                    />
                </Transition>
                <div class="flex flex-column">
                    <Transition name="swap" mode="out-in">
                        <span :key="activityName" class="text-sm font-medium text-900 line-height-2">
                            {{ activityName }}
                        </span>
                    </Transition>
                    <span class="text-xs text-600">Cuenta actual</span>
                </div>
            </div>

            <!-- Otras cuentas -->
            <div class="p-1 mt-1">
                <button
                    v-for="acc in otherAccounts"
                    :key="acc.id"
                    class="acc-item w-full flex align-items-center p-2 border-round-md"
                    @click="switchAccount(acc)"
                >
                    <ActivityLogo :activity="acc" :imgClass="['acc-logo', { 'acc-logo--dense': dense }, 'mr-2']"
                                  initialsClass="mr-2" :size="dense ? 28 : 32" />
                    <span class="text-sm text-900">{{ acc.name }}</span>
                </button>
            </div>

            <div class="my-2" style="height:1px; background: var(--surface-border)"></div>

            <!-- Salir -->
            <button class="acc-item danger w-full flex align-items-center p-2 border-round-md" @click="logout">
                <i class="pi pi-sign-out mr-2"></i>
                <span class="text-sm">Salir</span>
            </button>
        </OverlayPanel>
    </div>
</template>

<script>
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import EconActivity from '../../models/econActivity';
import httpRequest from '../../service/httpRequest';
import ActivityLogo from './ActivityLogo.vue';

export default {
    name: 'AccountSwitcher',
    props: {
        dense: { type: Boolean, default: false }
    },
    components: { Button, OverlayPanel, ActivityLogo },
    data() {
        return {
            panelVisible: false,
            activities: [],
            showCheck: false,
            successMessage: ''
        };
    },
    computed: {
        currentActivity() {
            return this.$store.getters.defaultActivity;
        },
        // Clave del fade al cambiar de actividad o de logo.
        logoKey() {
            const a = this.currentActivity || {};
            return `${a.id}:${a.logoUrl}`;
        },
        activityName() {
            // Prioriza el nombre comercial del negocio (capturado en el alta); si no hay, cae al
            // nombre de la actividad por defecto. El negocio no cambia al conmutar de actividad.
            const business = this.$store.getters.business;
            return (business && business.commercialName) || this.$store.getters.defaultActivity.name;
        },
        otherAccounts() {
            return this.activities.filter(
                (activity) => activity.id !== this.$store.getters.defaultActivity.id
            );
        }
    },
    created() {
        this.getEconActivities();
    },
    methods: {
        toggle(event) {
            this.$refs.panel.toggle(event);
        },
        async switchAccount(activity) {
            const ok = await this.selectEconActivity(activity);
            this.$refs.panel.hide();
            if (!ok) return;

            // Recargar la página (sea cual sea la vista actual) para cargar la data
            // de la actividad seleccionada. Tras el reload, App.vue re-sincroniza el
            // store con la nueva actividad por defecto del usuario (userExtension).
            window.location.reload();
        },
        async getEconActivities() {
            const response = await httpRequest.getEconActivities();
            if (response.status == 200) {
                const activitiesData = response.data.activities;
                this.activities = activitiesData.map((activity) => new EconActivity(activity));
            } else {
                this.activities = [this.$store.getters.defaultActivity];
            }
        },
        async selectEconActivity(activty) {
            const response = await httpRequest.setDefaultEconActivity(activty.id);
            if (response.status === 200) {
                const activity = new EconActivity(response.data);
                // Actualiza store -> dispara fade-in por cambio de computed
                await this.$store.dispatch('setDefaultActivity', activity);
                return true;
            }
            return false;
        },
        logout() {
            this.$emit('logout');
            this.$refs.panel.hide();
        }
    }
};
</script>

<style scoped>
.account-btn {
    display: inline-flex;
    align-items: center;
    padding: 0.65rem 1rem;
    border: 1px solid var(--surface-border);
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08), 0 1px 1px rgba(15, 23, 42, 0.04);
    background: var(--surface-card);
    color: var(--text-color);
    max-width: 20rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.account-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--primary-100);
}

/* Variante compacta */
.account-btn--dense {
    padding: 0.4rem 0.75rem;
}

/* Logo rectangular */
.acc-logo {
    height: 32px;
    width: auto;
    object-fit: contain;
    margin-right: 0.75rem;
    display: block;
}
.acc-logo--dense {
    height: 28px;
    margin-right: 0.5rem;
}

/* Nombre */
.acc-name {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 0.95rem;
}
.acc-name--dense {
    font-size: 0.85rem;
}

/* Panel */
.account-panel {
    min-width: 18rem;
    border-radius: 14px;
    padding: 0.35rem;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
}

/* Items */
.acc-item {
    border: 1px solid transparent;
    background: transparent;
    cursor: pointer;
    color: var(--text-color);
    transition: background 0.15s ease, border-color 0.15s ease;
}
.acc-item:hover {
    background: var(--surface-100);
    border-color: var(--surface-200);
}
.acc-item.danger {
    color: var(--red-600);
}
.acc-item.danger:hover {
    background: var(--red-50);
    border-color: var(--red-100);
}

/* ===== Animación fade-in/slide para cambios (logo/nombre) ===== */
.swap-enter-from,
.swap-leave-to {
    opacity: 0;
    transform: translateY(4px);
}
.swap-enter-active,
.swap-leave-active {
    transition: opacity 0.2s ease, transform 0.24s ease;
}
.swap-enter-to,
.swap-leave-from {
    opacity: 1;
    transform: translateY(0);
}

/* ===== Check animado (aparece y se retira) ===== */
.acc-check {
    display: inline-flex;
    margin-left: 0.125rem;
    animation: popFade 0.9s ease forwards;
}
.acc-check .pi {
    font-size: 0.95rem;
    color: var(--green-600);
}
@keyframes popFade {
    0% {
        opacity: 0;
        transform: scale(0.6);
    }
    40% {
        opacity: 1;
        transform: scale(1.15);
    }
    70% {
        transform: scale(0.98);
    }
    100% {
        opacity: 1;
        transform: scale(1);
    }
}

/* Accesibilidad */
.sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
}
</style>
